import { useState, useCallback, useEffect } from "react";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { Toggle } from "../settings/Toggle";
import { SettingsSection } from "../settings/SettingsSection";
import { Avatar } from "../Avatar";
import { apiGet, apiPatch } from "../../lib/api";
import type { AvatarId } from "@jumpbean/shared";

export interface ChildSettingsModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** The child whose settings are being edited */
  child: {
    id: string;
    displayName: string;
    avatarId: string;
  } | null;
  /** Callback when settings are saved */
  onSave?: () => void;
}

interface ChildSettings {
  id: string;
  childId: string;
  playMode: "sequential" | "adaptive";
  adaptiveTargetSuccessRate: number;
  skipMasteredChallenges: boolean;
  minChallengesForAdaptation: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Modal for editing a child's play mode settings.
 * Allows parents to switch between sequential and adaptive modes
 * and configure adaptive learning parameters.
 *
 * @example
 * <ChildSettingsModal
 *   isOpen={showSettings}
 *   onClose={() => setShowSettings(false)}
 *   child={selectedChild}
 *   onSave={refetchChildren}
 * />
 */
export function ChildSettingsModal({
  isOpen,
  onClose,
  child,
  onSave,
}: ChildSettingsModalProps) {
  const [settings, setSettings] = useState<ChildSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingMode, setPendingMode] = useState<"sequential" | "adaptive" | null>(null);

  // Local state for form fields
  const [playMode, setPlayMode] = useState<"sequential" | "adaptive">("sequential");
  const [targetSuccessRate, setTargetSuccessRate] = useState(75);
  const [skipMastered, setSkipMastered] = useState(false);
  const [minChallenges, setMinChallenges] = useState(5);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Fetch settings when modal opens or child changes
  useEffect(() => {
    if (isOpen && child) {
      fetchSettings();
    }
  }, [isOpen, child?.id]);

  const fetchSettings = async () => {
    if (!child) return;

    setIsLoading(true);
    setError(null);

    const { data, error: fetchError } = await apiGet<ChildSettings>(
      `/api/children/${child.id}/settings`
    );

    if (fetchError) {
      setError(fetchError.message || "Failed to load settings");
    } else if (data) {
      setSettings(data);
      setPlayMode(data.playMode);
      setTargetSuccessRate(Math.round(data.adaptiveTargetSuccessRate * 100));
      setSkipMastered(data.skipMasteredChallenges);
      setMinChallenges(data.minChallengesForAdaptation);
    }

    setIsLoading(false);
  };

  const handleClose = useCallback(() => {
    if (!isSaving) {
      setError(null);
      setShowConfirmation(false);
      setPendingMode(null);
      setShowAdvanced(false);
      onClose();
    }
  }, [isSaving, onClose]);

  const handleModeToggle = (isAdaptive: boolean) => {
    const newMode = isAdaptive ? "adaptive" : "sequential";

    // Show confirmation when switching TO adaptive mode
    if (newMode === "adaptive" && playMode === "sequential") {
      setPendingMode(newMode);
      setShowConfirmation(true);
    } else {
      setPlayMode(newMode);
    }
  };

  const confirmModeChange = () => {
    if (pendingMode) {
      setPlayMode(pendingMode);
      setShowConfirmation(false);
      setPendingMode(null);
    }
  };

  const cancelModeChange = () => {
    setShowConfirmation(false);
    setPendingMode(null);
  };

  const handleSave = async () => {
    if (!child) return;

    setIsSaving(true);
    setError(null);

    const { error: saveError } = await apiPatch<ChildSettings>(
      `/api/children/${child.id}/settings`,
      {
        playMode,
        adaptiveTargetSuccessRate: targetSuccessRate / 100,
        skipMasteredChallenges: skipMastered,
        minChallengesForAdaptation: minChallenges,
      }
    );

    if (saveError) {
      setError(saveError.message || "Failed to save settings");
    } else {
      onSave?.();
      handleClose();
    }

    setIsSaving(false);
  };

  // Check if settings have changed
  const hasChanges =
    settings &&
    (playMode !== settings.playMode ||
      targetSuccessRate !== Math.round(settings.adaptiveTargetSuccessRate * 100) ||
      skipMastered !== settings.skipMasteredChallenges ||
      minChallenges !== settings.minChallengesForAdaptation);

  if (!child) return null;

  // Mode change confirmation dialog
  if (showConfirmation) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={cancelModeChange}
        title="Enable Smart Mode?"
        size="sm"
        closeOnBackdropClick={false}
        closeOnEscape={false}
      >
        <div className="flex flex-col gap-6">
          {/* Child avatar and message */}
          <div className="text-center">
            <div className="mx-auto mb-4">
              <Avatar avatarId={child.avatarId as AvatarId} size="xl" />
            </div>
            <p className="font-display font-bold text-body-xl text-neutral-800 mb-2">
              {child.displayName}
            </p>
            <p className="font-body text-body-md text-neutral-600">
              This will personalize challenges based on {child.displayName}'s performance,
              keeping them in their learning sweet spot!
            </p>
          </div>

          {/* Info box */}
          <div className="p-4 bg-gradient-to-br from-magic-50 to-fairy-50 rounded-xl border-2 border-magic-200">
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">✨</span>
              <div>
                <p className="font-body text-body-sm text-neutral-700">
                  <strong>How it works:</strong> We'll track which skills {child.displayName} is
                  good at and which need more practice, then automatically pick challenges
                  that are just right - not too easy, not too hard!
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={confirmModeChange}
            >
              Yes, Enable Smart Mode
            </Button>

            <Button
              variant="ghost"
              size="md"
              fullWidth
              onClick={cancelModeChange}
            >
              Keep Sequential Mode
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Learning Settings"
      size="lg"
      closeOnBackdropClick={!isSaving}
      closeOnEscape={!isSaving}
    >
      <div className="flex flex-col gap-6">
        {/* Child Header */}
        <div className="flex items-center gap-4 pb-4 border-b border-neutral-100">
          <Avatar avatarId={child.avatarId as AvatarId} size="lg" />
          <div>
            <h3 className="font-display font-bold text-display-sm text-neutral-800">
              {child.displayName}
            </h3>
            <p className="font-body text-body-md text-neutral-500">
              Customize how challenges are selected
            </p>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="py-8 text-center">
            <div className="inline-block w-8 h-8 border-4 border-magic-300 border-t-magic-500 rounded-full animate-spin" />
            <p className="font-body text-body-md text-neutral-500 mt-3">
              Loading settings...
            </p>
          </div>
        ) : (
          <>
            {/* Play Mode Selection */}
            <SettingsSection
              title="Challenge Mode"
              description="Choose how challenges are selected"
              icon={<ModeIcon />}
              accent="magic"
            >
              <div className="space-y-4">
                {/* Mode Toggle */}
                <Toggle
                  checked={playMode === "adaptive"}
                  onChange={handleModeToggle}
                  label={playMode === "adaptive" ? "Smart Mode" : "Sequential Mode"}
                  description={
                    playMode === "adaptive"
                      ? "Challenges adapt to your child's skill level"
                      : "Challenges follow a fixed order, level by level"
                  }
                />

                {/* Mode Explanation Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {/* Sequential Card */}
                  <div
                    className={`
                      p-4 rounded-xl border-2 transition-all cursor-pointer
                      ${
                        playMode === "sequential"
                          ? "border-magic-400 bg-magic-50"
                          : "border-neutral-200 bg-neutral-50 hover:border-neutral-300"
                      }
                    `}
                    onClick={() => setPlayMode("sequential")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setPlayMode("sequential")}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">📚</span>
                      <span className="font-display font-semibold text-body-md text-neutral-800">
                        Sequential
                      </span>
                    </div>
                    <p className="font-body text-body-sm text-neutral-600">
                      Great for systematic learning - challenges in order, level by level
                    </p>
                  </div>

                  {/* Adaptive Card */}
                  <div
                    className={`
                      relative p-4 rounded-xl border-2 transition-all cursor-pointer overflow-hidden
                      ${
                        playMode === "adaptive"
                          ? "border-meadow-400 bg-meadow-50"
                          : "border-neutral-200 bg-neutral-50 hover:border-neutral-300"
                      }
                    `}
                    onClick={() => handleModeToggle(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleModeToggle(true)}
                  >
                    {/* NEW Badge */}
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 text-xs font-display font-bold bg-gradient-to-r from-sunshine-400 to-fairy-400 text-white rounded-full">
                        NEW
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">✨</span>
                      <span className="font-display font-semibold text-body-md text-neutral-800">
                        Smart Mode
                      </span>
                    </div>
                    <p className="font-body text-body-sm text-neutral-600">
                      Personalized challenge selection based on your child's abilities
                    </p>
                  </div>
                </div>
              </div>
            </SettingsSection>

            {/* Advanced Settings (collapsed by default, only for adaptive mode) */}
            {playMode === "adaptive" && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 w-full py-2 font-body font-medium text-body-md text-neutral-600 hover:text-neutral-800 transition-colors"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-90" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  Advanced Settings
                </button>

                {showAdvanced && (
                  <SettingsSection
                    title="Fine-Tune Learning"
                    description="Adjust how Smart Mode works"
                    icon={<TuneIcon />}
                    accent="sky"
                  >
                    <div className="space-y-6">
                      {/* Target Success Rate Slider */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">🎯</span>
                            <div>
                              <label className="block font-body font-semibold text-body-lg text-neutral-800">
                                Challenge Difficulty
                              </label>
                              <p className="font-body text-body-sm text-neutral-500">
                                Target success rate for challenges
                              </p>
                            </div>
                          </div>
                          <span className="font-display font-bold text-body-lg text-sky-600 min-w-[4rem] text-right">
                            {targetSuccessRate}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={65}
                          max={85}
                          step={5}
                          value={targetSuccessRate}
                          onChange={(e) => setTargetSuccessRate(parseInt(e.target.value, 10))}
                          className="w-full h-3 bg-neutral-200 rounded-full appearance-none cursor-pointer accent-sky-500"
                        />
                        <div className="flex justify-between text-xs font-body text-neutral-400">
                          <span>More challenging (65%)</span>
                          <span>Easier (85%)</span>
                        </div>
                      </div>

                      {/* Skip Mastered Toggle */}
                      <Toggle
                        checked={skipMastered}
                        onChange={setSkipMastered}
                        label="Skip Mastered Challenges"
                        description="Automatically skip challenges your child has already mastered"
                      />

                      {/* Warm-up Period */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">🌡️</span>
                          <div>
                            <label className="block font-body font-semibold text-body-lg text-neutral-800">
                              Warm-up Period
                            </label>
                            <p className="font-body text-body-sm text-neutral-500">
                              Challenges before full adaptation kicks in
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            min={1}
                            max={20}
                            step={1}
                            value={minChallenges}
                            onChange={(e) => setMinChallenges(parseInt(e.target.value, 10))}
                            className="flex-1 h-3 bg-neutral-200 rounded-full appearance-none cursor-pointer accent-sky-500"
                          />
                          <span className="font-display font-bold text-body-lg text-sky-600 min-w-[3rem] text-right">
                            {minChallenges}
                          </span>
                        </div>
                      </div>
                    </div>
                  </SettingsSection>
                )}
              </div>
            )}

            {/* Info Box for Adaptive Mode */}
            {playMode === "adaptive" && (
              <div className="p-4 bg-gradient-to-br from-sunshine-50 to-fairy-50 rounded-xl border border-sunshine-200">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">💡</span>
                  <div>
                    <p className="font-display font-semibold text-body-md text-neutral-800 mb-1">
                      Zone of Proximal Development
                    </p>
                    <p className="font-body text-body-sm text-neutral-600">
                      Smart Mode uses educational research to keep {child.displayName} in their
                      "learning sweet spot" - challenges that are just right for growth!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div
                className="
                  px-4 py-3
                  bg-fairy-50
                  border-2 border-fairy-300
                  rounded-xl
                  text-center
                "
                role="alert"
              >
                <p className="font-body text-body-md text-fairy-700 font-medium">
                  {error}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-2">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleSave}
                isLoading={isSaving}
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </Button>

              <Button
                variant="ghost"
                size="md"
                fullWidth
                onClick={handleClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Mode icon for the play mode section */
function ModeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  );
}

/** Tune icon for advanced settings */
function TuneIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
      />
    </svg>
  );
}

export default ChildSettingsModal;
